import { fetchAPI } from '.'

import {
  AdminNotificationPreferencesResponse,
  DomainCapturePolicy,
  DomainVerificationInitiateResponse,
  DomainVerificationStatus,
  ListDomainVerificationsResponse,
  UpdateAdminNotificationPreferencesRequest,
  UpdateDomainCapturePolicyRequest,
  UserStatusResponse,
} from '@/api/generated/api'
import {
  BillingTier,
  Enterprise,
  EnterpriseSettings,
  Member,
  NotetakerBlockedDomain,
  SettingType,
  TeamDomainStatus,
} from '@/types'

export interface CreateEnterpriseRequestNew {
  name: string
  autoInviteUsers: boolean
}

export interface UpdateEnterpriseRequest {
  name?: string
  logo?: string
}

export interface UpdateEnterpriseResponse {
  id: string
  name: string
  logo: string | null
}

/**
 * Write shape for a settings PATCH. Identical to `EnterpriseSettings` except for
 * `notetakerBlockedDomains`, which is sent as free-text strings (`*@acme.com`,
 * `bob@acme.com`) and comes back as canonical `{kind, value, policy}` objects —
 * the server is the only classifier, so reads stay strictly typed as objects.
 */
export type EnterpriseSettingsPatch = Omit<EnterpriseSettings, SettingType.notetakerBlockedDomains> & {
  [SettingType.notetakerBlockedDomains]?: string[] | NotetakerBlockedDomain[] | null
}

export interface UpdateEnterpriseSettingsRequest {
  settings: EnterpriseSettingsPatch
  /**
   * Confirms entries that would also block the org's own verified domains or a
   * consumer mail provider. Without it, such an entry returns 409.
   */
  acknowledgeSelfBlock?: boolean
}

export interface SignHipaaBaaRequest {
  legal_name: string
}

export async function getUserEnterprise() {
  return fetchAPI<Enterprise>({
    method: 'GET',
    url: '',
  })
}

export interface GetTeamDomainStatusResponse {
  team_domain_status: TeamDomainStatus
}

export async function getTeamDomainStatus() {
  return fetchAPI<GetTeamDomainStatusResponse>({
    method: 'GET',
    url: '/domain-status',
  })
}

export interface GetDomainInfoResponse {
  same_domain_users: Member[]
  team_members?: Member[]
  team_name?: string
  subscription_status?: string
}

export async function getDomainInfo() {
  return fetchAPI<GetDomainInfoResponse>({
    method: 'GET',
    url: '/domain-info',
  })
}

export async function updateEnterprise(data: UpdateEnterpriseRequest) {
  return fetchAPI<UpdateEnterpriseResponse>({
    method: 'PATCH',
    url: '',
    data: data,
  })
}

export async function createEnterpriseNew(data: CreateEnterpriseRequestNew) {
  return fetchAPI<Enterprise>({
    method: 'POST',
    url: '/create',
    data,
  })
}

export async function updateEnterpriseSettings(data: UpdateEnterpriseSettingsRequest) {
  return fetchAPI<EnterpriseSettings>({
    method: 'PATCH',
    url: '/settings',
    data,
    // The only 409 on this route is the self-block confirmation, which the admin
    // answers in a dialog — a routine round trip, not a fault.
    expectedStatuses: [409],
  })
}

export interface MyCurrentIpResponse {
  ip: string | null
}

export async function getMyCurrentIp() {
  return fetchAPI<MyCurrentIpResponse>({
    method: 'GET',
    url: '/my-current-ip',
  })
}

export async function setupSSO() {
  return fetchAPI<{ sso_admin_portal_url: string }>({
    method: 'POST',
    url: '/sso',
  })
}

export async function setupSCIM() {
  return fetchAPI<{ sso_admin_portal_url: string }>({
    method: 'GET',
    url: '/scim',
  })
}

export async function connectSSO() {
  return fetchAPI<{ message: string; detail?: string; provider?: string }>({
    method: 'GET',
    url: '/sso/connect',
  })
}

export async function fetchUserStatus(email: string) {
  return fetchAPI<UserStatusResponse>({
    method: 'GET',
    url: `/user_status?email=${encodeURIComponent(email)}`,
  })
}

export async function signEnterpriseHipaaBaa(data: SignHipaaBaaRequest) {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/hipaa-baa',
    data,
  })
}

export async function revokeEnterpriseHipaaBaa() {
  return fetchAPI<{ message: string }>({
    method: 'DELETE',
    url: '/hipaa-baa',
  })
}

export async function enableNotetakerEarlyAccess() {
  return fetchAPI<void>({
    method: 'POST',
    url: '/notetaker/early-access',
  })
}

// Join Request interfaces and functions
export interface AcceptEnterpriseInviteRequest {
  invite_id: string
}

export interface AcceptJoinRequestRequest {
  join_request_id: string
}

export interface BulkAcceptJoinRequestRequest {
  join_request_ids: string[]
}

export interface DeleteJoinRequestRequest {
  join_request_id: string
}

export type JoinRequestResponse = {
  id: string
  user_id: string
  user_email: string
  user_first_name: string
  user_last_name: string
  user_avatar_url: string
  enterprise_id: string
  created_at: string
  modified_at: string
  teamTrialGiven?: boolean
  trialEndsAt?: string
  hasIndividualSubscription: boolean | null
}[]

export async function acceptEnterpriseInvite(data: AcceptEnterpriseInviteRequest) {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/invitations/accept',
    data,
  })
}

export async function getMyInvitations() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchAPI<{ invitations: any[] }>({
    method: 'GET',
    url: '/invitations/my',
  })
}

export async function listJoinRequests() {
  return fetchAPI<JoinRequestResponse>({
    method: 'GET',
    url: '/join-requests',
  })
}

export async function listMyJoinRequests() {
  return fetchAPI<JoinRequestResponse>({
    method: 'GET',
    url: '/join-requests/my',
  })
}

export async function approveJoinRequest(data: AcceptJoinRequestRequest) {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/join-requests/accept',
    data,
  })
}

export async function bulkApproveJoinRequests(data: BulkAcceptJoinRequestRequest) {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/join-requests/bulk-accept',
    data,
  })
}

export async function deleteJoinRequest(data: DeleteJoinRequestRequest) {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/join-requests/delete',
    data,
  })
}

export async function checkEligibility(email: string) {
  return fetchAPI<{
    status:
      | 'unregistered'
      | 'already_in_enterprise'
      | 'has_individual_subscription'
      | 'has_mobile_subscription'
      | 'no_trial_available'
      | 'eligible_for_team_trial'
  }>({
    method: 'POST',
    url: '/check-eligibility',
    data: { email },
  })
}

export enum SeatType {
  ACTIVE = 'ACTIVE',
  TRIAL = 'TRIAL',
  NONE = 'NONE',
}

export type PlanTypeString =
  | 'NONE'
  | 'FLOW_PRO_NO_BILLING'
  | 'FLOW_PRO_MONTHLY'
  | 'FLOW_PRO_YEARLY'
  | 'FLOW_STUDENT_MONTHLY'
  | 'FLOW_STUDENT_YEARLY'
  | 'FLOW_TEAM_MONTHLY'
  | 'FLOW_TEAM_YEARLY'
  | 'FLOW_BUSINESS_MONTHLY'
  | 'FLOW_BUSINESS_YEARLY'

export interface MySeatInfoResponse {
  plan_type: PlanTypeString
  seat_type: 'ACTIVE' | 'TRIAL' | 'NONE'
  trial_ends_at?: number
  tier?: BillingTier
}

export async function getMySeatInfo() {
  return fetchAPI<MySeatInfoResponse>({
    method: 'GET',
    url: '/my-seat-info',
  })
}

export async function rejoinEnterprise() {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/rejoin',
  })
}

// ---------------------------------------------------------------------------
// DNS-based domain verification (enterprise admin self-serve)
// ---------------------------------------------------------------------------

export async function listDomainVerifications() {
  return fetchAPI<ListDomainVerificationsResponse>({
    method: 'GET',
    url: '/domains/verifications',
  })
}

export async function updateDomainCapturePolicy(domainId: string, policy: DomainCapturePolicy) {
  return fetchAPI<DomainVerificationStatus>({
    method: 'PATCH',
    url: `/domains/${encodeURIComponent(domainId)}/capture-policy`,
    data: { policy } satisfies UpdateDomainCapturePolicyRequest,
  })
}

export async function initiateDomainVerification(domainId: string) {
  return fetchAPI<DomainVerificationInitiateResponse>({
    method: 'POST',
    url: `/domains/${encodeURIComponent(domainId)}/initiate-verification`,
  })
}

export async function checkDomainVerification(domainId: string) {
  return fetchAPI<DomainVerificationStatus>({
    method: 'POST',
    url: `/domains/${encodeURIComponent(domainId)}/check-verification`,
  })
}

// ---------------------------------------------------------------------------
// Per-admin notification preferences
// ---------------------------------------------------------------------------

export async function getNotificationPreferences() {
  return fetchAPI<AdminNotificationPreferencesResponse>({
    method: 'GET',
    url: '/notification-preferences',
  })
}

export async function updateNotificationPreferences(data: UpdateAdminNotificationPreferencesRequest) {
  return fetchAPI<AdminNotificationPreferencesResponse>({
    method: 'PATCH',
    url: '/notification-preferences',
    data,
  })
}
